/**
 * dev-pdp-fulfillment.js
 *
 * Drives the PDP "Standard / Expedited Fulfillment" dates from estimate-delivery.js.
 *
 * A hidden <estimate-delivery class="dev-pdp-estimate"> in the product section runs
 * the cart-eta estimate for the selected variant and fills its hidden .jsDeliveryDate
 * spans (.dev-pdp-eta-targets, jsVariantSku path). This script:
 *   1. Re-runs the estimate on variant change — the reliable signal is the main
 *      product form's variant id (Horizon keeps it current), mapped to a SKU via a
 *      JSON island (<script type="application/json" class="dev-pdp-sku-map">). It also
 *      syncs the variant id onto the driver's data-id, because the cart-eta API keys
 *      off that lineId (a stale id returns the previous variant's estimate).
 *   2. Copies the resolved dates into the visible richtext blocks (.stand-fullfill /
 *      .expedited-fullfill), revealing a row only when it has a real date.
 */
(function () {
  function init(driver) {
    var section = driver.closest('.shopify-section');
    if (!section) return;

    var skuByVariantId = {};
    var mapEl = section.querySelector('.dev-pdp-sku-map');
    if (mapEl) {
      try {
        skuByVariantId = JSON.parse(mapEl.textContent) || {};
      } catch (e) {}
    }

    var skuInput = driver.querySelector('#VariantSkuForEstimateDelivery') || driver.querySelector('.jsVariantSku');

    function currentSku() {
      var idInput = section.querySelector('[name="id"]');
      var mapped = idInput ? skuByVariantId[idInput.value] : null;
      if (mapped) return mapped;
      return skuInput ? skuInput.value || skuInput.getAttribute('value') : null;
    }

    // A sold-out variant has no fulfillment date to promise — same signal the
    // native add-to-cart button already reacts to on variant change, so this
    // stays correct without needing our own id → availability map.
    function isSoldOut() {
      var addButton = section.querySelector('button[name="add"]');
      return !!addButton && addButton.disabled;
    }

    // --- Re-estimate on variant change ---
    var lastSku = currentSku();
    var timer;
    function reestimate() {
      var sku = currentSku();
      if (!sku || sku === lastSku) return;
      lastSku = sku;
      // Debounce so the section morph settles (the <estimate-delivery> element may be
      // replaced and must re-register its result listener before we refresh).
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (isSoldOut()) {
          // Sold out: never had a real fulfillment date to begin with — hide the
          // rows and skip the API call outright instead of fetching and discarding.
          hideEta();
          return;
        }
        var idInput = section.querySelector('[name="id"]');
        if (skuInput) {
          skuInput.value = currentSku() || sku;
          if (idInput && idInput.value) skuInput.setAttribute('data-id', idInput.value);
        }
        // 'variant:changed' (not 'refresh:estimate-delivery') — the latter's handler in
        // estimate-delivery.js hardcodes scope:"all", which re-fetches EVERY mounted
        // <estimate-delivery> instance on the page, including a cart drawer's, even
        // though only this PDP's own selection changed. 'variant:changed' maps to
        // scope:"pdp", which #getLineItems uses to skip any instance with no PDP sku
        // input (i.e. a cart-scoped one).
        window.dispatchEvent(new CustomEvent('variant:changed'));
      }, 200);
    }

    document.addEventListener('variant:update', reestimate);
    document.addEventListener('variant:selected', reestimate);
    section.addEventListener('change', reestimate);
    new MutationObserver(reestimate).observe(section, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['value'],
    });

    // --- Copy the resolved dates into the visible fulfillment blocks ---
    var targets = driver.querySelector('.dev-pdp-eta-targets') || section.querySelector('.dev-pdp-eta-targets');

    function etaText(el) {
      if (!el) return '';
      var eta = el.querySelector('.jsDeliveryDate-eta');
      return (eta ? eta.textContent : el.textContent || '').trim();
    }

    function setDate(selector, date) {
      var wrap = section.querySelector(selector);
      if (!wrap) return;
      var p = wrap.querySelector('p');
      if (!p) return;
      var label = (p.textContent.split(':')[0] || '').trim();
      if (date) {
        var next = label + ': ' + date;
        if (p.textContent.trim() !== next) p.textContent = next;
        wrap.classList.add('dev-eta-ready');
      } else {
        // No date for this variant — hide the row and clear any stale date.
        wrap.classList.remove('dev-eta-ready');
        if (p.textContent.trim() !== label) p.textContent = label;
      }
    }

    function hideEta() {
      setDate('.stand-fullfill', null);
      setDate('.expedited-fullfill', null);
    }

    function apply() {
      if (!targets) return;
      // Guards against a late-resolving fetch for a variant the shopper has since
      // switched away from (reestimate() already skips the fetch going forward,
      // but an in-flight one from before the switch can still land here).
      if (isSoldOut()) {
        hideEta();
        return;
      }
      setDate('.stand-fullfill', etaText(targets.querySelector('[data-method-id="standard"]')));
      setDate('.expedited-fullfill', etaText(targets.querySelector('[data-method-id="expedited"]')));
    }

    if (targets) {
      new MutationObserver(apply).observe(targets, { subtree: true, childList: true, characterData: true });
    }
    apply();
  }

  function boot() {
    document.querySelectorAll('.dev-pdp-estimate').forEach(function (driver) {
      if (driver.__pdpFulfillmentWired) return;
      driver.__pdpFulfillmentWired = true;
      init(driver);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
